import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { TestName as TestNameType, DiagnosisCategory } from '../../types/diagnosis';
import { useCurrency } from '../../hooks/useCurrency';
import toast from 'react-hot-toast';

export default function TestName() {
  const { formatCurrency } = useCurrency();
  const [tests, setTests] = useState<TestNameType[]>([]);
  const [categories, setCategories] = useState<DiagnosisCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingTest, setEditingTest] = useState<TestNameType | null>(null);
  const [formData, setFormData] = useState({
    diagnosis_category_id: '',
    test_name: '',
    price: '',
    discount_status: false,
    note: '',
  });

  useEffect(() => {
    loadCategories();
    loadTests();
  }, []);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('diagnosis_categories')
        .select('*')
        .order('service_head', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadTests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('test_names')
        .select(`
          *,
          category:diagnosis_categories(*)
        `)
        .order('test_name', { ascending: true });

      if (error) throw error;
      setTests(data || []);
    } catch (error: any) {
      console.error('Error loading tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const dataToSave = {
        ...formData,
        price: parseFloat(formData.price) || 0,
      };

      if (editingTest) {
        const { error } = await supabase
          .from('test_names')
          .update(dataToSave)
          .eq('id', editingTest.id);

        if (error) throw error;
        toast.success('Test updated successfully');
      } else {
        const { error } = await supabase
          .from('test_names')
          .insert([dataToSave]);

        if (error) throw error;
        toast.success('Test added successfully');
      }

      setShowForm(false);
      setEditingTest(null);
      setFormData({
        diagnosis_category_id: '',
        test_name: '',
        price: '',
        discount_status: false,
        note: '',
      });
      loadTests();
    } catch (error: any) {
      console.error('Error saving test:', error);
      toast.error(error.message || 'Failed to save test');
    }
  };

  const handleEdit = (test: TestNameType) => {
    setEditingTest(test);
    setFormData({
      diagnosis_category_id: test.diagnosis_category_id,
      test_name: test.test_name,
      price: test.price.toString(),
      discount_status: test.discount_status,
      note: test.note || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this test?')) return;

    try {
      const { error } = await supabase
        .from('test_names')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Test deleted successfully');
      loadTests();
    } catch (error: any) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };

  const filteredTests = tests.filter((test) => {
    const matchesSearch = test.test_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || test.diagnosis_category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Test Names</h1>
        <p className="text-gray-600">Manage diagnostic tests with pricing</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 flex-1">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search tests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">--Select Category--</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.diagnosis_category}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingTest(null);
              setFormData({
                diagnosis_category_id: '',
                test_name: '',
                price: '',
                discount_status: false,
                note: '',
              });
            }}
            className="ml-4 flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Test
          </button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingTest ? 'Edit Test' : 'Add New Test'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diagnosis Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.diagnosis_category_id}
                    onChange={(e) => setFormData({ ...formData, diagnosis_category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">--Select--</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.diagnosis_category}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.test_name}
                    onChange={(e) => setFormData({ ...formData, test_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={formData.discount_status === true}
                        onChange={() => setFormData({ ...formData, discount_status: true })}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={formData.discount_status === false}
                        onChange={() => setFormData({ ...formData, discount_status: false })}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTest(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingTest ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredTests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No tests found
                  </td>
                </tr>
              ) : (
                filteredTests.map((test, index) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {test.category?.diagnosis_category || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {test.test_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(test.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {test.discount_status ? 'Yes' : 'No'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(test)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(test.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
